import React from "react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import { useFetch } from "@/lib/useData";
import { Reveal, Stagger, Item } from "@/components/Reveal";
import { Search, FileText, CheckCircle2, Heart, ArrowUpRight } from "lucide-react";

const FALLBACK_STEPS = [
  { icon: "search",  title: "Choose your service or division", body: "Browse our Medical Center, Medical Lab and Pharmacy & Wellness divisions to find the right care for your needs." },
  { icon: "fileText",title: "Send a consultation request",    body: "Submit the contact form with your details, or call us directly. Tell us briefly what you need and your preferred time." },
  { icon: "check",   title: "We confirm within 24 hours",     body: "An Infinity Medical Group team member will reach out to confirm your appointment and answer any preparatory questions." },
  { icon: "heart",   title: "Visit us — we take care of the rest", body: "Arrive at the confirmed time. Our team welcomes you, walks you through any paperwork, and delivers your service with care." },
];
const ICONS = { search: Search, fileText: FileText, check: CheckCircle2, heart: Heart };

export default function Booking() {
  const { data } = useFetch("/pages/booking");
  const steps = (data?.steps && data.steps.length) ? data.steps : FALLBACK_STEPS;
  return (
    <>
      <SEO title="Book a consultation in Somalia | Infinity Medical Group" description="Book a medical, diagnostic or pharmacy consultation with Infinity Medical Group in Somalia. Four simple steps — choose your service, send a request, receive confirmation within 24 hours, and visit us for care." path="/booking" />
      <Breadcrumbs />
      <section className="bg-white pt-6 pb-10">
        <div className="container max-w-4xl">
          <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-teal">— How it works</p>
          <h1 className="mt-3 font-display font-bold text-4xl md:text-5xl lg:text-6xl text-navy-800 tracking-tight">Book your consultation</h1>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-navy-700/80">
            Four simple steps from enquiry to care. We confirm every appointment personally so you always know what to expect.
          </p>
        </div>
      </section>
      <section className="bg-soft-gray py-16 lg:py-24">
        <div className="container max-w-5xl">
          <Stagger className="grid gap-6 md:grid-cols-2">
            {steps.map((s, i) => {
              const Icon = ICONS[s.icon] || CheckCircle2;
              return (
                <Item key={i}>
                  <div className="p-7 rounded-3xl bg-white border border-navy-50 shadow-soft h-full" data-testid={`booking-step-${i + 1}`}>
                    <div className="flex items-center gap-3">
                      <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-teal/10 text-teal">
                        <Icon className="h-5 w-5" />
                      </span>
                      <span className="text-[11px] uppercase tracking-[0.22em] font-semibold text-navy-500">Step {i + 1}</span>
                    </div>
                    <h2 className="mt-5 font-display font-semibold text-xl text-navy-800">{s.title}</h2>
                    <p className="mt-3 text-sm leading-relaxed text-navy-700/80">{s.body}</p>
                  </div>
                </Item>
              );
            })}
          </Stagger>
          <Reveal className="mt-12 text-center">
            <Link to="/contact?booking=1" data-testid="booking-cta-contact" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-navy text-white text-sm font-semibold hover:bg-teal transition-colors">
              Start your booking <ArrowUpRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
